import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Save } from "lucide-react";

const GROUPS = [
  { key: "hero", fields: ["hero_title_ar", "hero_title_en", "hero_sub_ar", "hero_sub_en"], image: "hero_image" },
  { key: "about", fields: ["about_intro_ar", "about_intro_en", "about_philosophy_ar", "about_philosophy_en", "about_vision_ar", "about_vision_en", "about_mission_ar", "about_mission_en", "about_values_ar", "about_values_en", "about_slogan_ar", "about_slogan_en"] },
];

export const SiteSettings = () => {
  const { t } = useApp();
  const a = t.admin;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50/60 focus:border-tigris outline-none text-sm";

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)).catch(() => setForm({})); }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { const { data } = await api.put("/admin/settings", form); setForm(data); toast.success(a.updated); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, hero_image: `${process.env.REACT_APP_BACKEND_URL}${data.url}` })); toast.success(a.uploaded);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    finally { setUploading(false); e.target.value = ""; }
  };
  if (!form) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-tigris" size={36} /></div>;

  return (
    <form onSubmit={save} data-testid="site-settings-form" className="space-y-6">
      {GROUPS.map((g) => (
        <section key={g.key} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <h3 className="font-bold text-slate-800 mb-4">{a.settings[g.key]}</h3>
          {g.image && (
            <div className="mb-4">
              <label className="block text-[11px] font-semibold text-slate-500 mb-1">{a.settings.heroImage}</label>
              <div className="flex items-center gap-3">
                {form.hero_image && <img src={form.hero_image} alt="" className="w-28 h-16 rounded-lg object-cover border border-slate-200" data-testid="hero-image-preview" />}
                <input data-testid="settings-hero_image" className={inputCls} value={form.hero_image || ""} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} />
                <label data-testid="settings-hero-upload" className="btn-primary px-3 py-2 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap flex items-center gap-1">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {a.upload}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="settings-hero-file" />
                </label>
              </div>
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {g.fields.map((k) => (
              <div key={k}>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1">{a.settings.fields[k] || k}</label>
                {k.includes("title") || k.includes("slogan") || k.includes("values") ? (
                  <input data-testid={`settings-${k}`} className={inputCls} dir={k.endsWith("_en") ? "ltr" : "rtl"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                ) : (
                  <textarea data-testid={`settings-${k}`} rows={3} className={inputCls} dir={k.endsWith("_en") ? "ltr" : "rtl"} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={a.settings.emptyHint} />
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
      <button type="submit" disabled={saving} data-testid="settings-save-btn" className="btn-gold px-8 py-3 rounded-xl font-bold flex items-center gap-2">
        {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} {a.save}
      </button>
    </form>
  );
};
